import * as XLSX from 'xlsx';

/**
 * Field definitions for Excel export
 * Each field has: key (from monthlySchedule), label (display name)
 */
const EXPORT_FIELDS = [
  // Balance Sheet Items
  { key: 'loan_balance', label: 'Loan Balance' },
  { key: 'line_balance', label: 'Line Balance (Funded)' },
  { key: 'line_commitment_balance', label: 'Line Commitment Balance' },
  { key: 'deposit_balance', label: 'Deposit Balance' },
  { key: 'total_assets', label: 'Total Assets' },
  { key: 'total_liabilities', label: 'Total Liabilities' },
  { key: 'total_equity', label: 'Total Equity' },

  // Interest Income
  { key: 'interest_income_loans', label: 'Interest Income - Loans' },
  { key: 'interest_income_lines', label: 'Interest Income - Lines' },

  // Fee Income
  { key: 'origination_fees', label: 'Origination Fees' },
  { key: 'unused_commitment_fees', label: 'Unused Commitment Fees' },
  { key: 'non_interest_income', label: 'Non-Interest Income' },

  // Expenses
  { key: 'interest_expense', label: 'Interest Expense' },
  { key: 'salary_expense', label: 'Salary Expense' },
  { key: 'benefits_expense', label: 'Benefits Expense' },
  { key: 'incentive_compensation_expense', label: 'Incentive Compensation' },
  { key: 'discretionary_expense', label: 'Discretionary Expense' },
  { key: 'provision_expense', label: 'Provision Expense' },
  { key: 'non_interest_expense', label: 'Non-Interest Expense' },

  // Summary Metrics
  { key: 'net_interest_income', label: 'Net Interest Income' },
  { key: 'net_income', label: 'Net Income' },
  { key: 'cumulative_profit', label: 'Cumulative Profit' },
  { key: 'efficiency_ratio', label: 'Efficiency Ratio' },
];

/**
 * Format date from ISO string to Month-Year format
 */
const formatMonthHeader = (isoDate) => {
  const date = new Date(isoDate);
  return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short' });
};

/**
 * Generate filename with proper formatting
 */
const generateFilename = (name, isConsolidated) => {
  const date = new Date().toISOString().split('T')[0]; // YYYY-MM-DD
  const safeName = isConsolidated
    ? 'Consolidated'
    : name.replace(/[^a-zA-Z0-9]/g, '_'); // Sanitize name for filename
  return `ProForma_${safeName}_${date}.xlsx`;
};

/**
 * Export monthly schedule data to Excel
 * @param {Array} monthlySchedule - Array of monthly data objects
 * @param {string} name - RM name or "Consolidated"
 * @param {boolean} isConsolidated - Whether this is consolidated view
 */
export const exportToExcel = (monthlySchedule, name, isConsolidated = false) => {
  if (!monthlySchedule || monthlySchedule.length === 0) {
    console.error('No data to export');
    return;
  }

  // Build the data matrix: rows = fields, columns = months
  const headers = ['Metric', ...monthlySchedule.map(m => formatMonthHeader(m.index))];

  const rows = EXPORT_FIELDS.map(field => {
    const row = [field.label];
    monthlySchedule.forEach(month => {
      const value = month[field.key];
      // Handle null/undefined, format numbers appropriately
      row.push(value !== null && value !== undefined ? value : '');
    });
    return row;
  });

  // Create worksheet data with headers
  const worksheetData = [headers, ...rows];

  // Create worksheet from array of arrays
  const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);

  // Set column widths
  worksheet['!cols'] = [
    { wch: 30 }, // Metric column
    ...monthlySchedule.map(() => ({ wch: 14 })) // Date columns
  ];

  // Create workbook and add worksheet
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Monthly Pro Forma');

  // Generate filename and trigger download
  const filename = generateFilename(name, isConsolidated);
  XLSX.writeFile(workbook, filename);
};

/**
 * Check if export is available (results exist)
 */
export const canExport = (result) => {
  return result &&
         result.monthlySchedule &&
         Array.isArray(result.monthlySchedule) &&
         result.monthlySchedule.length > 0;
};
